import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { tr } from 'mmfintech-commons'
import { configuration, PaymentMethods, useWalletPayment } from 'mmfintech-checkout-commons'

import { StyledForm } from '../WalletCheckout.styled'
import { SelectAccount } from '../../../components'
import { PaymentContainer } from '../../CheckoutPayment/CheckoutPaymentCore.styled'
import { Button, ErrorDisplay } from 'mmfintech-portal-commons'

import type { AccountBalanceResponse } from 'mmfintech-checkout-commons/types'

export const WalletCheckoutAccount = ({
  pis,
  selectedAccount,
  setSelectedAccount,
  onSuccess,
  onCancel,
  paymentMethod
}) => {
  const { session, walletAccounts, walletAccountsError, walletPreviewError, walletPreviewFetching } = useSelector(
    ({
      checkout: {
        session,
        walletAccounts,
        walletAccountsError,
        walletAccountsFetching,
        walletPreviewError,
        walletPreviewFetching
      }
    }) => ({
      session,
      walletAccounts,
      walletAccountsError,
      walletAccountsFetching,
      walletPreviewError,
      walletPreviewFetching
    }),
    shallowEqual
  )
  const { onDemandCustomerDetails, isSubsequent, customerEmail, amount, currency } = session || {}

  const [filteredAccounts, setFilteredAccounts] = useState<AccountBalanceResponse[]>([])

  const { fetchAccounts, walletPaymentPreview } = useWalletPayment(pis)

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (selectedAccount) {
      const method = paymentMethod === PaymentMethods.INSTANT_BANK_TRANSFER ? PaymentMethods.JETON : paymentMethod
      walletPaymentPreview(selectedAccount.id, method, onSuccess)
    }
  }

  useEffect(() => {
    if (session) {
      const { currency } = session
      fetchAccounts(currency)
    }
    // eslint-disable-next-line
  }, [session])

  useEffect(() => {
    if (Array.isArray(walletAccounts)) {
      setFilteredAccounts(walletAccounts.filter(account => !account.prepaidCardAccount))
    }
  }, [walletAccounts])

  const hasSuitableAccount: boolean = useMemo(() => {
    return Boolean(
      filteredAccounts?.some(account => account.currencyCode === currency && account.availableBalance >= amount)
    )
  }, [filteredAccounts, amount, currency])

  const preparePortalLink = (path: string): string => {
    const host = configuration.readBackendConfig().replace('pay', 'portal')
    return `${host}${path.substring(0, 1) === '/' ? '' : '/'}${path}`
  }

  return (
    <PaymentContainer>
      <StyledForm noValidate onSubmit={handleSubmit}>
        <div className='account-title'>{tr('CHECKOUT.PAYMENT.SELECT_BANK_ACCOUNT', 'Select Bank Account')}</div>
        <SelectAccount
          noShadow
          className='mb-3'
          accounts={filteredAccounts}
          label='Account'
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
        />

        {paymentMethod === PaymentMethods.JETON &&
          !hasSuitableAccount &&
          (isSubsequent === true || onDemandCustomerDetails !== null) && (
            <Button
              type='submit'
              color='primary'
              className='mb-1'
              text={tr('CHECKOUT.BUTTONS.JETON_TOPUP', 'Top up in Jeton')}
              onClick={() => {
                window.open(preparePortalLink(`/login?email=${encodeURIComponent(customerEmail || '')}`), '_blank')
              }}
              data-test='button-submit'
            />
          )}
        <ErrorDisplay error={[walletAccountsError, walletPreviewError]} />

        <Button
          type='submit'
          color='primary'
          className='mb-1'
          text={tr('CHECKOUT.BUTTONS.CONTINUE', 'Continue')}
          loading={walletPreviewFetching}
          disabled={!selectedAccount}
          data-test='button-submit'
        />
        {typeof onCancel === 'function' && (
          <Button
            type='button'
            color='secondary'
            text={tr('CHECKOUT.BUTTONS.BACK', 'Back')}
            disabled={walletPreviewFetching}
            onClick={onCancel}
            data-test='button-back'
          />
        )}
      </StyledForm>
    </PaymentContainer>
  )
}
